import type { HTMLAttributes } from 'react';
import styles from './Toggle.module.css';

type TrackingValue = string | number | boolean;

export interface ToggleProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /**
   * Whether the toggle is checked/on
   */
  checked?: boolean;
  /**
   * Callback when toggle state changes
   */
  onChange?: (checked: boolean) => void;
  /**
   * Whether the toggle is disabled
   */
  disabled?: boolean;
  /**
   * Size of the toggle
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Label text to display next to the toggle
   */
  label?: string;

  /**
   * OpenPanel event name to track automatically.
   * When provided, renders `data-track` on the underlying button.
   */
  trackEvent?: string;

  /**
   * Extra OpenPanel properties. Each key becomes a `data-*` attribute (kebab-case).
   */
  trackData?: Record<string, TrackingValue | null | undefined>;
}

export function Toggle({
  checked = false,
  onChange,
  disabled = false,
  size = 'md',
  label,
  trackEvent,
  trackData,
  className,
  ...props
}: ToggleProps) {
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const toggleClasses = [
    styles.toggle,
    styles[size],
    checked && styles.checked,
    disabled && styles.disabled,
    className
  ].filter(Boolean).join(' ');

  const trackingAttributes: Record<string, string> = {};
  if (trackEvent) {
    trackingAttributes['data-track'] = trackEvent;
    trackingAttributes['data-checked'] = String(checked);

    if (trackData) {
      for (const [key, value] of Object.entries(trackData)) {
        if (value == null) continue;
        const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
        trackingAttributes[attrKey] = String(value);
      }
    }
  }

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label} onClick={!disabled ? handleClick : undefined}>
          {label}
        </label>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        disabled={disabled}
        className={toggleClasses}
        onClick={handleClick}
        {...trackingAttributes}
        {...props}
      >
        <span className={styles.slider} />
      </button>
    </div>
  );
}

Toggle.displayName = 'Toggle';
